<div class="subscription-page">
  <div class="page-title">Manage Subscription</div>

  @if (isLoading) {
    <app-skeleton [type]="'arcgis-webmap'" class="mt-2"></app-skeleton>
  } @else if (
    subscription && subscription.plan_name !== "free_plan" && !isLoading
  ) {
    @if (subscription.status === "canceled") {
      <div class="upgrade-section">
        <p>
          You're currently on the Free plan. Upgrade to unlock premium features.
        </p>

        @if (isSubscriptionAdmin) {
          <button class="primary-btn" (click)="openPlans()">
            Upgrade Plan
          </button>
          <div class="admin-support-notice">
            <app-svg-icon
              class="support-icon"
              [iconName]="'info.svg'"
              [width]="'16px'"
              [height]="'16px'"
            ></app-svg-icon>
            <span>
              For any issues or queries related to your subscription, please
              contact the Deeproots Data Analytics Hub admin at
              <a href="mailto:admin@accdata.org" class="support-email-link"
                >admin&#64;accdata.org</a
              >.
            </span>
          </div>
        } @else {
          @if (subscriptionAdmins && subscriptionAdmins.length > 0) {
            <div class="upgrade-admin-section">
              <div class="upgrade-admin-header">
                <app-svg-icon
                  class="notice-icon"
                  [iconName]="'info.svg'"
                  [width]="'16px'"
                  [height]="'16px'"
                ></app-svg-icon>
                <span>Contact your subscription admin to upgrade:</span>
              </div>

              <div class="admin-contacts-grid centered">
                @for (
                  admin of subscriptionAdmins;
                  track admin.id || admin.email
                ) {
                  <div class="admin-contact-card">
                    <div class="admin-avatar">
                      {{ getAdminInitials(admin) }}
                    </div>
                    <div class="admin-info">
                      @if (hasAdminName(admin)) {
                        <span class="admin-name">{{
                          getAdminFullName(admin)
                        }}</span>
                        @if (admin.email) {
                          <a
                            class="admin-email-link"
                            [href]="'mailto:' + admin.email"
                            [title]="'Send email to ' + admin.email"
                          >
                            <svg
                              class="email-icon"
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <rect
                                width="20"
                                height="16"
                                x="2"
                                y="4"
                                rx="2"
                              ></rect>
                              <path
                                d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
                              ></path>
                            </svg>
                            <span>{{ admin.email }}</span>
                          </a>
                        }
                      } @else {
                        @if (admin.email) {
                          <a
                            class="admin-email-link admin-name-link"
                            [href]="'mailto:' + admin.email"
                            [title]="'Send email to ' + admin.email"
                          >
                            <svg
                              class="email-icon"
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <rect
                                width="20"
                                height="16"
                                x="2"
                                y="4"
                                rx="2"
                              ></rect>
                              <path
                                d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
                              ></path>
                            </svg>
                            <span>{{ admin.email }}</span>
                          </a>
                        } @else {
                          <span class="admin-name">Admin</span>
                        }
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          } @else {
            <div class="upgrade-contact-notice">
              <app-svg-icon
                class="notice-icon"
                [iconName]="'info.svg'"
                [width]="'16px'"
                [height]="'16px'"
              ></app-svg-icon>
              <span
                >Contact your subscription admin to upgrade this
                subscription.</span
              >
            </div>
          }
        }
      </div>
    }
    <div class="subscription-card">
      <div class="card-header">
        <h3>
          {{
            subscription.status === "trialing"
              ? "Trial Subscription"
              : subscription.status === "active"
                ? "Active Subscription"
                : "Inactive Subscription"
          }}
        </h3>

        <span
          class="status-badge"
          [class.active]="
            subscription.status === 'active' ||
            subscription.status === 'trialing'
          "
          [class.cancelled]="subscription.status === 'canceled'"
        >
          {{ subscription.status | titlecase }}
        </span>
      </div>

      <div class="subscription-details">
        <div class="detail">
          <div class="detail-label">Plan</div>
          <div class="detail-value">
            {{
              subscription.display_name
                ? (subscription.display_name | titlecase)
                : "-"
            }}
          </div>
        </div>

        @if (isSubscriptionAdmin) {
          <div class="detail">
            <div class="detail-label">Amount</div>
            <div class="detail-value">
              {{ subscription.amount ? "$" + subscription.amount : "-" }}
            </div>
          </div>
        }

        <div class="detail">
          <div class="detail-label">Billing Interval</div>
          <div class="detail-value">
            {{
              subscription.billing_interval
                ? (subscription.billing_interval | titlecase)
                : "-"
            }}
          </div>
        </div>

        @if (isSubscriptionAdmin) {
          <div class="detail">
            <div class="detail-label">Quantity</div>
            <div class="detail-value">
              {{ subscription.quantity || "-" }}
            </div>
          </div>
        }

        @if (subscription.is_trialing || subscription.status === "trialing") {
          <div class="detail">
            <div class="detail-label">Trial Ends</div>
            <div class="detail-value">
              {{
                subscription.trial_end
                  ? (subscription.trial_end | date: "mediumDate")
                  : "-"
              }}
            </div>
          </div>
        }

        <div class="detail">
          <div class="detail-label">Start Date</div>
          <div class="detail-value">
            {{
              subscription.current_period_start
                ? (subscription.current_period_start | date: "mediumDate")
                : "-"
            }}
          </div>
        </div>

        <div class="detail">
          <div class="detail-label">End Date</div>
          <div class="detail-value">
            {{
              subscription.current_period_end
                ? (subscription.current_period_end | date: "mediumDate")
                : "-"
            }}
          </div>
        </div>

        @if (isSubscriptionAdmin) {
          <div class="detail">
            <div class="detail-label">Auto Renew</div>
            <div class="detail-value">
              {{ subscription.cancel_at_period_end ? "Disabled" : "Enabled" }}
            </div>
          </div>
        }
      </div>

      @if (
        subscription &&
        subscription.cancel_at_period_end &&
        subscription.status === "active"
      ) {
        @if (isSubscriptionAdmin) {
          <div class="warning-box">
            <app-svg-icon
              class="warning-icon"
              [iconName]="'warning-circle.svg'"
              [width]="'18px'"
              [height]="'18px'"
            ></app-svg-icon>
            <div class="warning-content">
              <div class="warning-title">You cancelled your subscription.</div>
              <div class="warning-text">
                Your subscription will remain active until
                <strong>
                  {{
                    subscription.current_period_end | date: "MMM d, y"
                  }} </strong
                >.
              </div>
            </div>
          </div>
        } @else {
          <div class="subscription-info-box">
            <div class="info-header-row">
              <app-svg-icon
                class="info-icon-badge"
                [iconName]="'info.svg'"
                [width]="'20px'"
                [height]="'20px'"
              ></app-svg-icon>
              <div class="info-header-text">
                <div class="info-title">Subscription Notice</div>
                <div class="info-text">
                  Your subscription will remain active until
                  <strong>
                    {{
                      subscription.current_period_end | date: "MMM d, y"
                    }} </strong
                  >.
                </div>
              </div>
            </div>

            <ng-container *ngTemplateOutlet="infoAdminContact"></ng-container>
          </div>
        }
      }

      @if (subscription && subscription.webhook_status === "processing") {
        @if (isSubscriptionAdmin) {
          <div class="warning-box mt-2">
            <app-svg-icon
              class="warning-icon"
              [iconName]="'warning-circle.svg'"
              [width]="'18px'"
              [height]="'18px'"
            ></app-svg-icon>
            <div class="warning-content">
              <div class="warning-title">Subscription update in progress</div>
              <div class="warning-text">
                Your subscription is currently being updated. <br />This may
                take a few moments while we process the changes.<br />
                The page will automatically reflect the updated subscription
                once the process is complete.
              </div>
            </div>
          </div>
        } @else {
          <div class="subscription-info-box mt-2">
            <div class="info-header-row">
              <app-svg-icon
                class="info-icon-badge"
                [iconName]="'info.svg'"
                [width]="'20px'"
                [height]="'20px'"
              ></app-svg-icon>
              <div class="info-header-text">
                <div class="info-title">Subscription Update in Progress</div>
                <div class="info-text">
                  The subscription is currently being updated. Changes will be
                  reflected once processing is complete.
                </div>
              </div>
            </div>

            <ng-container *ngTemplateOutlet="infoAdminContact"></ng-container>
          </div>
        }
      }

      @if (subscription && subscription.pending_plan) {
        @if (isSubscriptionAdmin) {
          <div class="warning-box mt-2">
            <app-svg-icon
              class="warning-icon"
              [iconName]="'warning-circle.svg'"
              [width]="'18px'"
              [height]="'18px'"
            ></app-svg-icon>
            <div class="warning-content">
              <div class="warning-title">Your plan will be downgraded.</div>
              <div class="warning-text">
                Your subscription will remain on
                <strong>{{ subscription.display_name }}</strong>
                until
                <strong>
                  {{
                    subscription.current_period_end | date: "MMM d, y"
                  }} </strong
                >. It will automatically downgrade to
                <strong>{{
                  subscription.pending_plan_display_name
                    ? (subscription.pending_plan_display_name | titlecase)
                    : (subscription.pending_plan | titlecase)
                }}</strong>
                on the next billing cycle.
              </div>
            </div>
          </div>
        } @else {
          <div class="subscription-info-box mt-2">
            <div class="info-header-row">
              <app-svg-icon
                class="info-icon-badge"
                [iconName]="'info.svg'"
                [width]="'20px'"
                [height]="'20px'"
              ></app-svg-icon>
              <div class="info-header-text">
                <div class="info-title">Upcoming Plan Change</div>
                <div class="info-text">
                  The current plan will remain active until
                  <strong>
                    {{
                      subscription.current_period_end | date: "MMM d, y"
                    }} </strong
                  >, then switch to
                  <strong>{{
                    subscription.pending_plan_display_name
                      ? (subscription.pending_plan_display_name | titlecase)
                      : (subscription.pending_plan | titlecase)
                  }}</strong
                  >.
                </div>
              </div>
            </div>

            <ng-container *ngTemplateOutlet="infoAdminContact"></ng-container>
          </div>
        }
      }

      <ng-template #infoAdminContact>
        <div class="info-contact-divider"></div>
        <div class="info-contact-section">
          <div class="info-contact-header">
            <div class="info-contact-title">
              Contact your subscription admin for questions or changes
            </div>
            <div class="info-contact-subtitle">
              Only subscription admins can manage subscription plans and billing
              settings.
            </div>
          </div>

          @if (subscriptionAdmins && subscriptionAdmins.length > 0) {
            <div class="admin-contacts-grid">
              @for (
                admin of subscriptionAdmins;
                track admin.id || admin.email
              ) {
                <div class="admin-contact-card">
                  <div class="admin-avatar">{{ getAdminInitials(admin) }}</div>
                  <div class="admin-info">
                    @if (hasAdminName(admin)) {
                      <span class="admin-name">{{
                        getAdminFullName(admin)
                      }}</span>
                      @if (admin.email) {
                        <a
                          class="admin-email-link"
                          [href]="'mailto:' + admin.email"
                          [title]="'Send email to ' + admin.email"
                        >
                          <svg
                            class="email-icon"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              width="20"
                              height="16"
                              x="2"
                              y="4"
                              rx="2"
                            ></rect>
                            <path
                              d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
                            ></path>
                          </svg>
                          <span>{{ admin.email }}</span>
                        </a>
                      }
                    } @else {
                      @if (admin.email) {
                        <a
                          class="admin-email-link admin-name-link"
                          [href]="'mailto:' + admin.email"
                          [title]="'Send email to ' + admin.email"
                        >
                          <svg
                            class="email-icon"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              width="20"
                              height="16"
                              x="2"
                              y="4"
                              rx="2"
                            ></rect>
                            <path
                              d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
                            ></path>
                          </svg>
                          <span>{{ admin.email }}</span>
                        </a>
                      } @else {
                        <span class="admin-name">Admin</span>
                      }
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </ng-template>

      @if (isSubscriptionAdmin) {
        <div class="action-buttons">
          @if (
            !subscription.cancel_at_period_end &&
            subscription.pending_plan === null &&
            subscription.status !== "canceled" &&
            subscription.webhook_status === "processed"
          ) {
            <!-- Switch Plan -->
            <button class="primary-btn" (click)="openPlans()">
              <span>Switch Plan</span>

              <span class="tooltip-container">
                <app-svg-icon
                  class="info-icon"
                  [iconName]="'info.svg'"
                  [width]="'16px'"
                  [height]="'16px'"
                ></app-svg-icon>

                <span class="tooltip-text">
                  Upgrade or downgrade your subscription plan. Changes may take
                  effect immediately or at the end of your current billing
                  period.
                </span>
              </span>
            </button>
          }

          <!-- Manage Billing -->
          <button class="primary-btn" (click)="manageSubscription()">
            <span>Manage Billing</span>

            <span class="tooltip-container">
              <app-svg-icon
                class="info-icon"
                [iconName]="'info.svg'"
                [width]="'16px'"
                [height]="'16px'"
              ></app-svg-icon>

              <span class="tooltip-text">
                Open the Stripe Customer Portal to update payment methods, view
                invoices, download receipts, manage billing details, and update
                your subscription.
              </span>
            </span>
          </button>

          <!-- Transaction History -->
          <button class="secondary-btn" (click)="viewTransactions()">
            <span>Transaction History</span>

            <span class="tooltip-container">
              <app-svg-icon
                class="info-icon"
                [iconName]="'info.svg'"
                [width]="'16px'"
                [height]="'16px'"
              ></app-svg-icon>

              <span class="tooltip-text">
                View your invoices, payment history, receipts, and transaction
                status.
              </span>
            </span>
          </button>

          @if (
            !subscription.cancel_at_period_end &&
            subscription.status === "active"
          ) {
            <button class="danger-btn" (click)="cancelSubscription()">
              <span>Cancel Subscription</span>

              <span class="tooltip-container">
                <app-svg-icon
                  class="info-icon"
                  [iconName]="'info.svg'"
                  [width]="'16px'"
                  [height]="'16px'"
                ></app-svg-icon>

                <span class="tooltip-text">
                  Cancel your subscription. Your access will remain active until
                  the end of the current billing period.
                </span>
              </span>
            </button>
          }
        </div>

        <div class="admin-support-notice">
          <app-svg-icon
            class="support-icon"
            [iconName]="'info.svg'"
            [width]="'16px'"
            [height]="'16px'"
          ></app-svg-icon>
          <span>
            For any issues or queries related to your subscription, please
            contact the Deeproots Data Analytics Hub admin at
            <a href="mailto:admin@accdata.org" class="support-email-link"
              >admin&#64;accdata.org</a
            >.
          </span>
        </div>
      } @else if (!hasActiveWarning) {
        @if (subscriptionAdmins && subscriptionAdmins.length > 0) {
          <div class="admin-contact-banner">
            <div class="admin-contact-header">
              <app-svg-icon
                class="admin-contact-icon"
                [iconName]="'info.svg'"
                [width]="'18px'"
                [height]="'18px'"
              ></app-svg-icon>
              <div class="admin-contact-header-content">
                <span class="admin-contact-title"
                  >Contact your subscription admin to manage this
                  subscription.</span
                >
                <span class="admin-contact-subtitle"
                  >Only subscription admins can switch plans, cancel, or modify
                  billing.</span
                >
              </div>
            </div>

            <div class="admin-contacts-grid">
              @for (
                admin of subscriptionAdmins;
                track admin.id || admin.email
              ) {
                <div class="admin-contact-card">
                  <div class="admin-avatar">{{ getAdminInitials(admin) }}</div>
                  <div class="admin-info">
                    @if (hasAdminName(admin)) {
                      <span class="admin-name">{{
                        getAdminFullName(admin)
                      }}</span>
                      @if (admin.email) {
                        <a
                          class="admin-email-link"
                          [href]="'mailto:' + admin.email"
                          [title]="'Send email to ' + admin.email"
                        >
                          <svg
                            class="email-icon"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              width="20"
                              height="16"
                              x="2"
                              y="4"
                              rx="2"
                            ></rect>
                            <path
                              d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
                            ></path>
                          </svg>
                          <span>{{ admin.email }}</span>
                        </a>
                      }
                    } @else {
                      @if (admin.email) {
                        <a
                          class="admin-email-link admin-name-link"
                          [href]="'mailto:' + admin.email"
                          [title]="'Send email to ' + admin.email"
                        >
                          <svg
                            class="email-icon"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              width="20"
                              height="16"
                              x="2"
                              y="4"
                              rx="2"
                            ></rect>
                            <path
                              d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
                            ></path>
                          </svg>
                          <span>{{ admin.email }}</span>
                        </a>
                      } @else {
                        <span class="admin-name">Admin</span>
                      }
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        } @else {
          <div class="info-message">
            <app-svg-icon
              class="info-message-icon"
              [iconName]="'info.svg'"
              [width]="'18px'"
              [height]="'18px'"
            ></app-svg-icon>
            <span>
              Contact your subscription admin to manage this subscription.
            </span>
          </div>
        }
      }
    </div>
  } @else {
    <div class="upgrade-section">
      <p>
        You're currently on the Free plan. Upgrade to unlock premium features.
      </p>

      @if (isSubscriptionAdmin) {
        <button class="primary-btn" (click)="openPlans()">Upgrade Plan</button>
        <div class="admin-support-notice">
          <app-svg-icon
            class="support-icon"
            [iconName]="'info.svg'"
            [width]="'16px'"
            [height]="'16px'"
          ></app-svg-icon>
          <span>
            For any issues or queries related to your subscription, please
            contact the Deeproots Data Analytics Hub admin at
            <a href="mailto:admin@accdata.org" class="support-email-link"
              >admin&#64;accdata.org</a
            >.
          </span>
        </div>
      } @else {
        @if (subscriptionAdmins && subscriptionAdmins.length > 0) {
          <div class="upgrade-admin-section">
            <div class="upgrade-admin-header">
              <app-svg-icon
                class="notice-icon"
                [iconName]="'info.svg'"
                [width]="'16px'"
                [height]="'16px'"
              ></app-svg-icon>
              <span>Contact your subscription admin to upgrade:</span>
            </div>

            <div class="admin-contacts-grid centered">
              @for (
                admin of subscriptionAdmins;
                track admin.id || admin.email
              ) {
                <div class="admin-contact-card">
                  <div class="admin-avatar">{{ getAdminInitials(admin) }}</div>
                  <div class="admin-info">
                    @if (hasAdminName(admin)) {
                      <span class="admin-name">{{
                        getAdminFullName(admin)
                      }}</span>
                      @if (admin.email) {
                        <a
                          class="admin-email-link"
                          [href]="'mailto:' + admin.email"
                          [title]="'Send email to ' + admin.email"
                        >
                          <svg
                            class="email-icon"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              width="20"
                              height="16"
                              x="2"
                              y="4"
                              rx="2"
                            ></rect>
                            <path
                              d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
                            ></path>
                          </svg>
                          <span>{{ admin.email }}</span>
                        </a>
                      }
                    } @else {
                      @if (admin.email) {
                        <a
                          class="admin-email-link admin-name-link"
                          [href]="'mailto:' + admin.email"
                          [title]="'Send email to ' + admin.email"
                        >
                          <svg
                            class="email-icon"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              width="20"
                              height="16"
                              x="2"
                              y="4"
                              rx="2"
                            ></rect>
                            <path
                              d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
                            ></path>
                          </svg>
                          <span>{{ admin.email }}</span>
                        </a>
                      } @else {
                        <span class="admin-name">Admin</span>
                      }
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        } @else {
          <div class="upgrade-contact-notice">
            <app-svg-icon
              class="notice-icon"
              [iconName]="'info.svg'"
              [width]="'16px'"
              [height]="'16px'"
            ></app-svg-icon>
            <span
              >Contact your subscription admin to upgrade this
              subscription.</span
            >
          </div>
        }
      }
    </div>
  }

  @if (showSubscriptionPlansPopup) {
    <div class="plans-modal-overlay">
      <div class="plans-modal">
        <button
          type="button"
          class="close-btn"
          (click)="showSubscriptionPlansPopup = false"
          aria-label="Close"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <div class="plans-header">
          <h2>Choose Your Plan</h2>
          <p>Pick the plan that fits your organization. Change anytime.</p>

          <div
            class="billing-toggle"
            role="group"
            aria-label="Billing interval"
          >
            <button
              type="button"
              [class.active]="billingInterval === 'month'"
              (click)="billingInterval = 'month'"
            >
              Monthly
            </button>

            <button
              type="button"
              [class.active]="billingInterval === 'year'"
              (click)="billingInterval = 'year'"
            >
              Yearly
            </button>
          </div>
        </div>

        <div class="plans-grid">
          @for (plan of getVisiblePlans(); track plan.plan_name) {
            <div
              class="plan-card"
              [class.current-plan-card]="
                subscription.plan_name === plan.plan_name
              "
            >
              @if (subscription.plan_name === plan.plan_name) {
                <span class="current-badge">Current</span>
              }

              <div class="plan-top">
                <h3>{{ plan.display_name | titlecase }}</h3>
              </div>

              <div class="price-section">
                @if (getPlanPrice(plan, billingInterval); as price) {
                  <div class="price-row">
                    <span class="currency">$</span>
                    <span class="amount">{{ price.amount }}</span>
                  </div>
                  <span class="period">
                    per {{ billingInterval === "month" ? "month" : "year" }}
                  </span>
                } @else {
                  <span class="amount">-</span>
                }
              </div>

              <button
                type="button"
                class="plan-btn"
                [class.current-plan]="plan.plan_name === subscription.plan_name"
                [disabled]="plan.plan_name === subscription.plan_name"
                (click)="switchPlan(plan)"
              >
                {{
                  plan.plan_name === subscription.plan_name
                    ? "Current Plan"
                    : "Switch Plan"
                }}
              </button>

              <div class="features-divider"></div>

              <div class="features-title">What's included</div>

              <ul class="feature-list">
                @for (feature of plan.marketing_features; track feature) {
                  <li>{{ feature }}</li>
                } @empty {
                  <li class="empty-feature">No features listed.</li>
                }
              </ul>
            </div>
          }
        </div>

        @if (isDowngradePopupOpen) {
          <app-popup
            [heading]="isDowngrade ? 'Confirm Downgrade' : 'Confirm Upgrade'"
            [confirmText]="isDowngrade ? 'Downgrade' : 'Upgrade'"
            [cancelText]="'Cancel'"
            (confirmClicked)="manageSwitchPlan()"
            (cancelClicked)="isDowngradePopupOpen = false"
          >
            @if (isDowngrade) {
              Your subscription will remain on
              <strong>{{ subscription.display_name | titlecase }}</strong>
              until
              <strong>
                {{
                  subscription.current_period_end | date: "MMM d, y"
                }} </strong
              >. It will automatically downgrade to
              <strong>{{ targetPlan?.display_name | titlecase }}</strong>
              on the next billing cycle.
            } @else {
              Your subscription will be upgraded immediately to
              <strong>{{ targetPlan?.display_name | titlecase }}.</strong><br />
              Any prorated charges or credits will be applied automatically
              according to your billing cycle.
            }
          </app-popup>
        }
      </div>
    </div>
  }
</div>
